/**
 * ImportFromVaultModal Component
 *
 * Modal for importing Evidence Vault documents into a job.
 * Shows available licenses, insurance, and certifications with multi-select.
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, Archive, FileCheck, Shield, BadgeCheck, AlertTriangle, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getReusableEvidenceDocuments,
  linkVaultDocumentToJob,
} from "@/services/evidenceVaultService";
import type { EvidenceDocument, EvidenceDocumentType } from "@/services/mock/evidenceVaultData";
import { useToast } from "@/hooks/use-toast";

interface ImportFromVaultModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobAddress: string;
  contractorId: string;
  onSuccess: () => void;
  existingDocumentIds?: string[];
}

const TYPE_CONFIG: Record<EvidenceDocumentType, { label: string; icon: typeof FileCheck }> = {
  license: { label: "Licenses", icon: BadgeCheck },
  insurance: { label: "Insurance", icon: Shield },
  authorization: { label: "Authorizations", icon: FileCheck },
  product_approval: { label: "Product Approvals", icon: FileCheck },
  permit_form: { label: "Permit Forms", icon: FileCheck },
  plan_set: { label: "Plan Sets", icon: FileCheck },
  calculation: { label: "Calculations", icon: FileCheck },
  inspection_evidence: { label: "Inspection Evidence", icon: FileCheck },
  city_correspondence: { label: "City Correspondence", icon: FileCheck },
  rejection_notice: { label: "Rejection Notices", icon: FileCheck },
  affidavit: { label: "Affidavits", icon: FileCheck },
  specification: { label: "Specifications", icon: FileCheck },
  other: { label: "Other", icon: FileCheck },
};

export function ImportFromVaultModal({
  open,
  onOpenChange,
  jobId,
  jobAddress,
  contractorId,
  onSuccess,
  existingDocumentIds = [],
}: ImportFromVaultModalProps) {
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [documents, setDocuments] = useState<EvidenceDocument[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      loadDocuments();
    }
  }, [open, contractorId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const docs = await getReusableEvidenceDocuments(contractorId);
      // Filter out documents already linked to this job
      const filteredDocs = docs.filter(
        d => !d.linkedJobIds.includes(jobId) && !existingDocumentIds.includes(d.id)
      );
      setDocuments(filteredDocs);
    } catch (error) {
      console.error("Failed to load vault documents:", error);
      toast({
        title: "Error",
        description: "Failed to load Evidence Vault documents",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleDocument = (docId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedIds(newSelected);
  };

  const handleImport = async () => {
    if (selectedIds.size === 0) return;

    setImporting(true);
    try {
      const selectedDocs = documents.filter(d => selectedIds.has(d.id));

      // Import each selected document
      for (const doc of selectedDocs) {
        await linkVaultDocumentToJob(contractorId, doc, jobId, jobAddress);
      }

      toast({
        title: "Documents Imported",
        description: `Successfully imported ${selectedDocs.length} document${selectedDocs.length > 1 ? 's' : ''} from Evidence Vault`,
      });

      setSelectedIds(new Set());
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to import documents:", error);
      toast({
        title: "Import Failed",
        description: "Some documents could not be imported. Please try again.",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  // Group documents by type
  const groupedDocs = documents.reduce((acc, doc) => {
    if (!acc[doc.type]) {
      acc[doc.type] = [];
    }
    acc[doc.type].push(doc);
    return acc;
  }, {} as Record<EvidenceDocumentType, EvidenceDocument[]>);

  const isExpiringSoon = (dateString?: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return date <= thirtyDaysFromNow && date > new Date();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5" />
            Import from Evidence Vault
          </DialogTitle>
          <DialogDescription>
            Select documents from your Evidence Vault to attach to this job.
            These documents will be linked but not duplicated.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Archive className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No documents available</p>
              <p className="text-sm mt-1">
                Upload licenses, insurance, or certifications to your Evidence Vault first.
              </p>
            </div>
          ) : (
            Object.entries(groupedDocs).map(([type, docs]) => {
              const config = TYPE_CONFIG[type as EvidenceDocumentType];
              const Icon = config.icon;

              return (
                <div key={type} className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Icon className="h-4 w-4" />
                    {config.label}
                  </div>
                  <div className="space-y-2">
                    {docs.map(doc => (
                      <label
                        key={doc.id}
                        className={cn(
                          "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                          selectedIds.has(doc.id)
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/50"
                        )}
                      >
                        <Checkbox
                          checked={selectedIds.has(doc.id)}
                          onCheckedChange={() => toggleDocument(doc.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">
                              {doc.name}
                            </span>
                            {isExpiringSoon(doc.expirationDate) && (
                              <Badge variant="outline" className="text-amber-600 border-amber-300 shrink-0">
                                <Clock className="h-3 w-3 mr-1" />
                                Expiring Soon
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {doc.fileName}
                          </p>
                          {doc.linkedJobIds.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Linked to {doc.linkedJobIds.length} other job{doc.linkedJobIds.length > 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importing}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={selectedIds.size === 0 || importing}
          >
            {importing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                Import {selectedIds.size > 0 ? `${selectedIds.size} ` : ""}Document{selectedIds.size !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ImportFromVaultModal;
